/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  Button,
  Breadcrumbs,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import { getStore, RootState } from '@ink-ai/portal/reducers';
import { glossaryExtractionActions } from '@ink-ai/portal/reducers/glossary-extraction';
import { app } from '@ink-ai/portal/reducers/app';
import { ReferenceFileUploadArea, ReferenceExtractionArea } from '../components/extraction/ReferenceExtractionArea';
import { GlossaryExtractionOptionDtoLanguagesEnum as Language } from '@ink-ai/insight-service-sdk';
import { compose } from '@ink-ai/portal/reducers/compose';
import { Link, useBlocker, useNavigate } from 'react-router-dom';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';

const confirmExit = () => {
  const glossaryExtraction = getStore().getState().glossaryExtraction;
  if (glossaryExtraction.glossaryExtractionStatus === 'uploading') {
    return 'The document is still in the process of uploading. Are you sure, you want to close?';
  }
};

export const ReferenceExtraction: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const store = getStore();
  const composeState = useSelector((state: RootState) => state.compose);
  const glossaryExtraction = useSelector((state: RootState) => state.glossaryExtraction);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      glossaryExtraction.glossaryExtractionStatus === 'uploading' &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    window.onbeforeunload = confirmExit;
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  useEffect(() => {
    dispatch(glossaryExtractionActions.setSelectedLanguages(['ZH_CN', 'EN_US'] as any));
    // Clean up on unmount
    return () => {
      dispatch(glossaryExtractionActions.clearAll());
      dispatch(compose.actions.clearAll());
    };
  }, []);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, language: string) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const simulatedEvent = {
        target: {
          files: files,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(simulatedEvent, language);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, language: string) => {
    const file = event.target.files?.[0];
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/pdf',
    ];

    if (file && allowedTypes.includes(file.type)) {
      dispatch(glossaryExtractionActions.setFileName(file.name));
      dispatch(glossaryExtractionActions.setSelectedFileForLanguage({ language, file }));
      dispatch(glossaryExtractionActions.setFileNameForLanguage({ language, fileName: file.name }));
      dispatch(glossaryExtractionActions.setFileSizeForLanguage({ language, fileSize: file.size }));
    } else {
      dispatch(glossaryExtractionActions.setSelectedFileForLanguage({ language, file: null }));
      store.dispatch(
        app.actions.setGlobalMessage({
          message: 'Invalid file type. Please upload a .docx or .pdf file.',
          status: 'error',
        }),
      );
    }
  };

  const handleExtractReference = async () => {
    try {
      dispatch(glossaryExtractionActions.setLoading(true));
      const trimmedName = (name || '').trim();
      if (!trimmedName) {
        setNameError(t('glossary.dialog.form.name.required'));
        dispatch(
          app.actions.setGlobalMessage({
            message: t('glossary.dialog.form.name.required'),
            status: 'error',
          }),
        );
        dispatch(glossaryExtractionActions.setLoading(false));
        return;
      }
      const selectedFiles = glossaryExtraction.selectedFiles || {};
      const hasSelectedFiles = Object.values(selectedFiles).some((file) => !!file);
      if (!hasSelectedFiles) {
        dispatch(
          app.actions.setGlobalMessage({
            message: 'No file selected.',
            status: 'error',
          }),
        );
        dispatch(glossaryExtractionActions.setLoading(false));
        return;
      }

      // Validate that when two files are present, their extensions match
      const filesArray = Object.values(selectedFiles).filter(Boolean) as File[];
      if (filesArray.length >= 2) {
        const getExt = (f: File) => {
          const name = f.name || '';
          const parts = name.split('.');
          return (parts.length > 1 ? parts.pop() : '')?.toLowerCase();
        };
        const exts = new Set(filesArray.map(getExt));
        if (exts.size > 1) {
          dispatch(
            app.actions.setGlobalMessage({
              message: t('referenceExtraction.import.sameTypeRequired'),
              status: 'error',
            }),
          );
          dispatch(glossaryExtractionActions.setLoading(false));
          return;
        }
      }

      const selectedLanguages = (glossaryExtraction.selectedLanguages || []) as unknown as Language[];
      const filename = glossaryExtraction.fileName || Object.values(selectedFiles)[0]?.name || 'reference.docx';

      const createResp = await makeRequest({
        method: 'POST',
        url: '/api/v1/reference-extraction',
        data: {
          filename,
          name: trimmedName,
          description,
          option: { languages: selectedLanguages },
        },
      });

      const uuid: string = createResp?.uuid || createResp?.data?.uuid;
      // 记录 uuid 以便 WS 回传时可以匹配并更新状态
      dispatch(glossaryExtractionActions.setUuid(uuid));
      const uploadUrls: Array<{ uploadUrl: string; language: string }> =
        createResp?.uploadUrls || createResp?.data?.uploadUrls || [];

      // Upload each language file to its presigned URL
      for (const { uploadUrl, language } of uploadUrls) {
        const file = (selectedFiles as Record<string, File | undefined>)[language];
        if (!file) continue;
        await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });
      }

      // Trigger extraction
      await makeRequest({
        method: 'POST',
        url: `/api/v1/reference-extraction/${uuid}/start`,
        data: {},
      });

      navigate('/extraction/reference/tasks');
    } catch (error: any) {
      console.error(error);
      const status = error?.status ?? error?.response?.status;
      const backendMessage = error?.response?.data?.message || error?.message || 'Failed to extract reference';
      const messageToShow = backendMessage;
      dispatch(app.actions.setGlobalMessage({ message: messageToShow, status: 'error' }));
    } finally {
      dispatch(glossaryExtractionActions.setLoading(false));
    }
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} kb`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} mb`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} gb`;
  };

  const handleLanguagesChange = (languages: Language[]) => {
    dispatch(glossaryExtractionActions.setSelectedLanguages(languages as any));
  };

  // Auto navigate back to the tasks list when extraction has started or completed
  useEffect(() => {
    const status = glossaryExtraction.glossaryExtractionStatus;
    if (status === 'extracting' || status === 'queueing' || status === 'completed') {
      navigate('/extraction/reference/tasks');
    }
  }, [glossaryExtraction.glossaryExtractionStatus]);

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t('referenceExtraction.navigation.home')}</Link>
        <Link to="/extraction/reference/tasks">{t('referenceExtraction.navigation.referenceExtractionTasks')}</Link>
        <Typography color="textPrimary">{t('referenceExtraction.navigation.new')}</Typography>
      </Breadcrumbs>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" sx={{ textAlign: 'left' }}>
          {t('referenceExtraction.import.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
          {t('referenceExtraction.import.description')}
        </Typography>
        <Box sx={{ width: '50%' }}>
        <TextField
          label={t('referenceExtraction.import.form.name')}
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={name}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => {
            const v = e.target.value;
            setName(v);
            if ((v || '').trim()) setNameError(null);
          }}
          required
          error={!!nameError}
          helperText={nameError || ''}
        />
          <TextField
            label={t('referenceExtraction.import.form.description')}
            variant="outlined"
            fullWidth
            sx={{ mb: 1 }}
            value={description}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>

        {glossaryExtraction.selectedLanguages.map((language, index) => (
          <ReferenceFileUploadArea
            key={index}
            glossaryExtraction={glossaryExtraction}
            language={language}
            handleDrop={(event) => handleDrop(event, language)}
            handleDragOver={handleDragOver}
            handleFileChange={(event) => handleFileChange(event, language)}
            handleRemoveFile={() => dispatch(glossaryExtractionActions.removeFileForLanguage({ language }))}
            formatFileSize={formatFileSize}
          />
        ))}

        <ReferenceExtractionArea
          glossaryExtraction={glossaryExtraction}
          handleExtractReference={handleExtractReference}
          handleDownload={async () => {}}
        />
      </Paper>

      <Dialog open={blocker.state === 'blocked'}>
        <DialogTitle>{t('referenceExtraction.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('referenceExtraction.dialog.content')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.proceed()}>{t('referenceExtraction.dialog.leave')}</Button>
          <Button onClick={() => blocker.reset()} color="primary" variant="contained" autoFocus>
            {t('referenceExtraction.dialog.stay')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

